import {
  useEffect,
  useCallback,
  useState,
  useRef,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@square/web-payments-sdk-types';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import {
  useGetSavedCards,
  useDeleteSavedCard,
} from 'typescript/hooks/use_payment_sources_client';
import { WalletPaymentSource } from 'typescript/types/api/payment_sources';
import { useToaster } from 'typescript/hooks/use_toaster';

interface UseSavedCardsProps {
  setShowCardForm: (show: boolean) => void;
  sqCard: Card;
  canUseSavedCard: boolean;
}

interface UseSavedCardsReturn {
  showSavedCards: boolean;
  savedCards: WalletPaymentSource[];
  savedCardsDropdownRef: MutableRefObject<HTMLDivElement | null>;
  deletionInProgress: boolean;
  handlers: {
    onCardSelected: (cardId: number) => void;
    onCardClicked: (
      event: ReactMouseEvent<HTMLMarketActionCardElement>,
    ) => void;
    onCardDeleted: (cardId: number) => void;
    showSavedCardsDropdown: (event: CustomEvent) => void;
    hideSavedCardsDropdown: () => void;
    handleClickOutside: (event: MouseEvent) => void;
  };
}

export const useSavedCards = ({
  setShowCardForm,
  sqCard,
  canUseSavedCard,
}: UseSavedCardsProps): UseSavedCardsReturn => {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  const [savedCards, setSavedCards] = useState<WalletPaymentSource[]>([]);
  const [showSavedCards, setShowSavedCards] = useState<boolean>(false);

  const savedCardsDropdownRef = useRef<HTMLDivElement | null>(null);

  const { orderNumber } = useCheckoutFlow();

  const { setPayWithSavedCard, setSelectedCard } = useSavedCardsContext();

  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    // error: orderDataError, // TODO: handle error state
  } = useGetOrderSummary({ orderNumber });

  const { savedCardsData, error: savedCardsDataError } = useGetSavedCards({
    email: orderSummaryData?.current_user?.email,
    merchantToken: orderSummaryData.current_user.merchant_token,
    enabled:
      !orderDataIsPending && Boolean(orderSummaryData) && canUseSavedCard,
  });

  if (savedCardsDataError) {
    console.error('Error fetching saved cards:', savedCardsDataError);
  }

  const {
    deleteSavedCard,
    isPending: deletionInProgress,
    error: deleteSavedCardError,
  } = useDeleteSavedCard({
    email: orderSummaryData?.current_user?.email,
    merchantToken: orderSummaryData.current_user.merchant_token,
  });

  const onCardSelected = useCallback(
    (cardId: number) => {
      const foundCard = savedCards.find(
        (card) => String(card.id) === String(cardId),
      );
      if (foundCard) {
        setSelectedCard(foundCard);
        setPayWithSavedCard(true);
        setShowCardForm(false);
      } else {
        setSelectedCard(null);
        setPayWithSavedCard(false);
      }
      setShowSavedCards(false);
    },
    [setShowCardForm, setSelectedCard, setShowSavedCards, savedCards],
  );

  const onCardDeleted = useCallback(
    (cardId: number) => {
      deleteSavedCard(
        { cardId },
        {
          onError: () => {
            showToast({
              message: t('checkout.payment.deleteSavedCardError'),
              variant: 'warning',
            });
            console.error('Error deleting saved card:', deleteSavedCardError);
          },
          onSettled: () => {
            setShowSavedCards(true);
          },
        },
      );
    },
    [setShowSavedCards, setSavedCards, savedCards],
  );

  const onCardClicked = useCallback(
    (event: ReactMouseEvent<HTMLMarketActionCardElement>) => {
      setShowCardForm(true);
      setSelectedCard(null);
      setPayWithSavedCard(false);
      if (sqCard) {
        // This `setTimeout` is to reduce the likelihood of calling the recalculateSize method
        // before the card form is fully rendered
        setTimeout(async () => {
          await sqCard.clear();
          await sqCard.focus('cardNumber');
          await sqCard.recalculateSize();
        }, 10);
      }
      // This is an ugly hack to make sure the "market action card" for the
      // selected card does not stay in the deselected state after it is clicked
      // eslint-disable-next-line no-param-reassign
      (event.target as HTMLMarketActionCardElement).selected = true;
    },
    [setShowCardForm, setSelectedCard, sqCard],
  );

  const showSavedCardsDropdown = useCallback(
    (event: CustomEvent) => {
      if (event.detail.field === 'cardNumber') {
        setShowSavedCards(true);
      }
    },
    [setShowSavedCards],
  );

  const hideSavedCardsDropdown = useCallback(() => {
    setShowSavedCards(false);
  }, [setShowSavedCards]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        savedCardsDropdownRef.current &&
        !savedCardsDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSavedCards(false);
      }
    },
    [setShowSavedCards],
  );

  useEffect(() => {
    if (savedCardsData?.wallet_payment_sources && canUseSavedCard) {
      const data = savedCardsData.wallet_payment_sources;
      setSavedCards(data);
      const defaultCard = data.find((card) => card.default);
      if (defaultCard) {
        setSelectedCard(defaultCard);
        setPayWithSavedCard(true);
        setShowCardForm(false);
      }

      if (sqCard) {
        sqCard.addEventListener('focusClassAdded', showSavedCardsDropdown);
        sqCard.addEventListener('cardBrandChanged', hideSavedCardsDropdown);
        sqCard.addEventListener('escape', hideSavedCardsDropdown);
      }
    }

    return () => {
      if (canUseSavedCard && sqCard) {
        sqCard.removeEventListener('focusClassAdded', showSavedCardsDropdown);
        sqCard.removeEventListener('cardBrandChanged', hideSavedCardsDropdown);
        sqCard.removeEventListener('escape', hideSavedCardsDropdown);
      }
    };
  }, [
    savedCardsData,
    canUseSavedCard,
    sqCard,
    showSavedCardsDropdown,
    hideSavedCardsDropdown,
  ]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return {
    showSavedCards,
    savedCards,
    savedCardsDropdownRef,
    deletionInProgress,
    handlers: {
      onCardSelected,
      onCardClicked,
      onCardDeleted,
      showSavedCardsDropdown,
      hideSavedCardsDropdown,
      handleClickOutside,
    },
  };
};
